import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit2, Trash2, FolderOpen, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Composant pour une catégorie draggable
function SortableCategory({ category, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      
      {/* Bouton Retour */}
      <BackButton label="Retour" />

<Card className={isDragging ? 'shadow-lg' : ''}>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-3 flex-1">
              {/* Drag handle */}
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing mt-1 touch-none"
                title="Glisser pour réorganiser"
              >
                <GripVertical className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-xl">{category.name}</CardTitle>
                  <Badge variant={category.is_active ? 'default' : 'secondary'}>
                    {category.is_active ? 'Active' : 'Archivée'}
                  </Badge>
                  <Badge className="bg-slate-700 text-white font-semibold">
                    {category.nomenclature_count || 0} nomenclature(s)
                  </Badge>
                </div>
                {category.description && (
                  <p className="text-sm text-gray-600 mt-2">{category.description}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">Ordre: {category.order}</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(category)}
              >
                <Edit2 className="h-4 w-4" />
              </Button>
              {category.is_active && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(category.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>
    </div>
  );
}

export default function CategoriesManagement() {
  const { getAuthHeader } = useAuth();
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    collection_id: '',
    name: '',
    description: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchCollections();
  }, []);

  useEffect(() => {
    if (selectedCollection) {
      fetchCategories();
    }
  }, [selectedCollection]);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/nomenclature-collections`, {
        headers: getAuthHeader()
      });
      setCollections(response.data);
      
      // Sélectionner la première collection active par défaut
      const activeCollection = response.data.find(c => c.is_active);
      if (activeCollection && !selectedCollection) {
        setSelectedCollection(activeCollection);
        setFormData(prev => ({ ...prev, collection_id: activeCollection.id }));
      }
    } catch {
      toast.error('Erreur lors du chargement des collections');
    }
  };

  const fetchCategories = async () => {
    if (!selectedCollection) return;
    
    try {
      const response = await axios.get(
        `${API}/categories?collection_id=${selectedCollection.id}`, 
        { headers: getAuthHeader() }
      );
      setCategories(response.data);
    } catch {
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        // Mise à jour
        await axios.put(
          `${API}/categories/${editingCategory.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Catégorie mise à jour avec succès');
      } else {
        // Création
        await axios.post(
          `${API}/categories`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Catégorie créée avec succès');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      collection_id: category.collection_id,
      name: category.name,
      description: category.description || '',
      order: category.order,
      is_active: category.is_active
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver cette catégorie ?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/categories/${categoryId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Catégorie archivée avec succès');
      fetchCategories();
    } catch {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = categories.findIndex((cat) => cat.id === active.id);
      const newIndex = categories.findIndex((cat) => cat.id === over.id);

      // Réorganiser localement pour une mise à jour immédiate
      const newCategories = arrayMove(categories, oldIndex, newIndex);
      setCategories(newCategories);

      // Mettre à jour les ordres dans la base de données
      try {
        // Mettre à jour l'ordre de toutes les catégories affectées
        const updates = newCategories.map((cat, index) => ({
          id: cat.id,
          order: index
        }));

        // Envoyer les mises à jour
        await Promise.all(
          updates.map((update) =>
            axios.put(
              `${API}/categories/${update.id}`,
              { order: update.order },
              { headers: getAuthHeader() }
            )
          )
        );

        toast.success('Ordre des catégories mis à jour');
      } catch {
        toast.error('Erreur lors de la mise à jour de l\'ordre');
        // Recharger les catégories en cas d'erreur
        fetchCategories();
      }
    }
  };

  const resetForm = () => {
    setFormData({
      collection_id: selectedCollection?.id || '',
      name: '',
      description: '',
      order: 0,
      is_active: true
    });
    setEditingCategory(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coppam-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sélecteur de Collection */}
      {collections.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-semibold">Collection active :</Label>
              <select
                value={selectedCollection?.id || ''}
                onChange={(e) => {
                  const collection = collections.find(c => c.id === e.target.value);
                  setSelectedCollection(collection);
                  setFormData(prev => ({ ...prev, collection_id: collection?.id || '' }));
                }}
                className="flex-1 max-w-sm px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {collections.filter(c => c.is_active).map(collection => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name} ({collection.categories_count || 0} catégories)
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>
      )}
      
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">
            Gestion des Catégories {selectedCollection ? `- ${selectedCollection.name}` : ''}
          </h2>
          <p className="text-gray-600 mt-1">Gérez les catégories de nomenclature du système</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Nouvelle Catégorie
            </Button>
          </DialogTrigger>
          
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nom de la catégorie *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Travaux Publics, Gros Oeuvre..."
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description de la catégorie..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="order">Ordre d'affichage</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Plus le numéro est petit, plus la catégorie apparaît en premier</p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="is_active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="is_active" className="cursor-pointer">Catégorie active</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Annuler
                </Button>
                <Button type="submit">
                  {editingCategory ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {categories.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-gray-500 py-4 md:py-8">
                <FolderOpen className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>Aucune catégorie trouvée</p>
                <p className="text-sm mt-2">Créez votre première catégorie pour commencer</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              💡 <strong>Astuce :</strong> Glissez et déposez les catégories pour les réorganiser
            </p>
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={categories.map((cat) => cat.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {categories.map((category) => (
                <SortableCategory
                  key={category.id}
                  category={category}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}
